/**
 * Global, cross-page LiveKit session context.
 *
 * - A single Room instance lives in context and survives React Router navigation.
 * - Subscribing components render their own video via `room.localParticipant` and
 *   `room.remoteParticipants` track attach().
 * - A `workout-sync` data channel lets any page (e.g. WorkoutDetailPage) broadcast
 *   and receive `{exIdx, curSet, timerRunning, timerAnchorMs, totalSeconds, sentAt}`
 *   so both buddies stay in sync — no matter which page they are on.
 *
 * Usage (from a button handler):
 *   const live = useLiveVideo();
 *   await live.startSession(sessionId);
 *   // The floating PiP appears globally; navigate freely.
 *   live.endSession(); // hangs up
 *
 * Usage (from WorkoutDetailPage):
 *   const live = useLiveVideo();
 *   useEffect(() => live.subscribe(handleIncomingState), [live]);
 *   // When local state changes:  live.publish({ exIdx, curSet, ... })
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Room, RoomEvent, Track } from 'livekit-client';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const LiveVideoContext = createContext(null);
const SYNC_TOPIC = 'workout-sync';

export function LiveVideoProvider({ children }) {
  const { api, refreshUser } = useAuth();
  const [session, setSession] = useState(null); // {sessionId, roomName, isHost, participantName}
  const [roomState, setRoomState] = useState({ room: null, connected: false });
  const listenersRef = useRef(new Set()); // Set<(payload, participantIdentity) => void>
  const audioMutedRef = useRef(false);
  const videoMutedRef = useRef(false);

  // Re-render hook for participant changes (tracks, connects, disconnects)
  const [, setTick] = useState(0);
  const bumpTick = useCallback(() => setTick((n) => n + 1), []);

  const attachRoomListeners = useCallback((room) => {
    const onData = (payload, participant, _kind, topic) => {
      // LiveKit JS sends data with an optional topic; fall back to payload inspection
      try {
        const text = new TextDecoder().decode(payload);
        const parsed = JSON.parse(text);
        if (topic && topic !== SYNC_TOPIC && parsed.__topic !== SYNC_TOPIC) return;
        listenersRef.current.forEach((fn) => {
          try { fn(parsed, participant?.identity); } catch { /* noop */ }
        });
      } catch { /* non-json payload, ignore */ }
    };
    const onParticipantChange = () => bumpTick();
    const onTrackChange = () => bumpTick();

    // Auto-attach remote audio tracks so buddies can hear each other.
    // LiveKit doesn't auto-render audio for us when we manage the Room manually.
    // Track elements are appended to <body> by livekit-client and removed on detach.
    const onTrackSubscribed = (track) => {
      onTrackChange();
      try {
        if (track?.kind === Track.Kind.Audio) {
          const el = track.attach();
          el.setAttribute('data-livekit-audio', '1');
          // hide it — audio plays via the element itself
          el.style.display = 'none';
          document.body.appendChild(el);
        }
      } catch { /* ignore — UI still renders */ }
    };
    const onTrackUnsubscribed = (track) => {
      onTrackChange();
      try {
        if (track?.kind === Track.Kind.Audio) {
          // detach() returns the elements that were attached; remove them.
          const els = track.detach();
          (Array.isArray(els) ? els : [els]).forEach((el) => {
            if (el && el.parentNode) el.parentNode.removeChild(el);
          });
        }
      } catch { /* ignore */ }
    };

    room.on(RoomEvent.DataReceived, onData);
    room.on(RoomEvent.ParticipantConnected, onParticipantChange);
    room.on(RoomEvent.ParticipantDisconnected, onParticipantChange);
    room.on(RoomEvent.TrackSubscribed, onTrackSubscribed);
    room.on(RoomEvent.TrackUnsubscribed, onTrackUnsubscribed);
    room.on(RoomEvent.LocalTrackPublished, onTrackChange);
    room.on(RoomEvent.LocalTrackUnpublished, onTrackChange);
    room.on(RoomEvent.ConnectionStateChanged, bumpTick);
  }, [bumpTick]);

  const startSession = useCallback(async (sessionId) => {
    if (roomState.room) {
      // Already in a session — ignore duplicate starts
      return { ok: true, alreadyActive: true };
    }
    try {
      const tokenRes = await api().post('/livekit/token', { session_id: sessionId });
      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
      });
      attachRoomListeners(room);
      await room.connect(tokenRes.data.server_url, tokenRes.data.access_token);
      await room.localParticipant.setCameraEnabled(true).catch(() => {});
      await room.localParticipant.setMicrophoneEnabled(true).catch(() => {});
      audioMutedRef.current = false;
      videoMutedRef.current = false;
      setRoomState({ room, connected: true });
      setSession({
        sessionId,
        roomName: tokenRes.data.room_name,
        isHost: !!tokenRes.data.is_host,
        participantName: tokenRes.data.participant_name,
      });
      // Self-heal: if the auth state was inadvertently cleared earlier (stale
      // 401 race), repopulate `user` from the still-valid HttpOnly cookie so
      // the rest of the app doesn't think we're logged out.
      if (typeof refreshUser === 'function') {
        refreshUser().catch(() => { /* noop */ });
      }
      toast.success('Live video connected');
      return { ok: true };
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Failed to start live video';
      toast.error(detail);
      return { ok: false, error: detail };
    }
  }, [api, attachRoomListeners, roomState.room]);

  const endSession = useCallback(async () => {
    const room = roomState.room;
    const currentSession = session;
    if (!room) return;
    try { await room.disconnect(true); } catch { /* noop */ }
    listenersRef.current.clear();
    setRoomState({ room: null, connected: false });
    setSession(null);
    // Mark the buddy session as ended in the backend so re-opening the workout
    // forces a fresh invite/accept flow. Also clear the auto-launch sessionStorage
    // flag so navigating back to the workout doesn't re-trigger auto-join.
    if (currentSession?.sessionId) {
      try {
        sessionStorage.removeItem(`as-auto-launch-${currentSession.sessionId}`);
      } catch { /* private mode or quota */ }
      try {
        await api().post(`/buddies/workout-invite/${currentSession.sessionId}/end-call`);
      } catch { /* silent — user is already disconnected client-side */ }
    }
  }, [roomState.room, session, api]);

  // Auto-cleanup on unmount / page unload
  useEffect(() => {
    const handler = () => {
      if (roomState.room) {
        try { roomState.room.disconnect(true); } catch { /* noop */ }
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [roomState.room]);

  // Data channel publish / subscribe
  const publish = useCallback((payload) => {
    const room = roomState.room;
    if (!room || !roomState.connected) return;
    try {
      const enriched = { ...payload, __topic: SYNC_TOPIC, sentAt: Date.now() };
      const bytes = new TextEncoder().encode(JSON.stringify(enriched));
      room.localParticipant.publishData(bytes, { reliable: true, topic: SYNC_TOPIC });
    } catch { /* silent */ }
  }, [roomState.room, roomState.connected]);

  const subscribe = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  const toggleMic = useCallback(async () => {
    const room = roomState.room;
    if (!room) return;
    const enable = audioMutedRef.current; // was muted → enable true
    try {
      await room.localParticipant.setMicrophoneEnabled(enable);
      audioMutedRef.current = !enable;
      bumpTick();
    } catch { /* silent */ }
  }, [roomState.room, bumpTick]);

  const toggleCam = useCallback(async () => {
    const room = roomState.room;
    if (!room) return;
    const enable = videoMutedRef.current; // was off → enable true
    try {
      await room.localParticipant.setCameraEnabled(enable);
      videoMutedRef.current = !enable;
      bumpTick();
    } catch { /* silent */ }
  }, [roomState.room, bumpTick]);

  const value = {
    session,
    room: roomState.room,
    connected: roomState.connected,
    startSession,
    endSession,
    publish,
    subscribe,
    toggleMic,
    toggleCam,
    isMicMuted: () => audioMutedRef.current,
    isCamOff: () => videoMutedRef.current,
  };

  return (
    <LiveVideoContext.Provider value={value}>
      {children}
    </LiveVideoContext.Provider>
  );
}

export function useLiveVideo() {
  const ctx = useContext(LiveVideoContext);
  if (!ctx) throw new Error('useLiveVideo must be used inside <LiveVideoProvider>');
  return ctx;
}
